import { inject, Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { LOCAL_STORAGE_CONSTANT } from '@constants/localstorage.constant';
import { ExivoComponentDbModel, StoreDbModel } from '@db-models/store-db.model';
import { WorkerService } from '@feature-services/worker.service';
import { CryptoService } from '@util-services/crypto.service';
import { HelperService } from '@util-services/helper.service';
import { HttpClientService } from '@util-services/http-client.service';
import { LocalStorageService } from '@util-services/local-storage.service';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class StoreService {

  private httpClientService = inject(HttpClientService);
  private localstorageService = inject(LocalStorageService);
  private cryptoService = inject(CryptoService);
  private workerService = inject(WorkerService);
  private helperService = inject(HelperService);

  /**
   * The cached copy is only invalidated by the browser that created, edited or deleted a
   * store. Another user's browser keeps serving its stale list until logout — and with the
   * store hierarchy on, a re-parent done by the admin changes which stores that user may see.
   * Pages where membership matters pass forceRefresh to bypass the cache and refill it.
   */
  all(forceRefresh = false): Observable<StoreDbModel[]> {
    const stores = forceRefresh ? null : this.getStoresFromLocalStorage();
    if (stores?.length) {
      return of(stores);
    }

    return this.httpClientService.get(`stores`, {})
      .pipe(
        switchMap((stores: StoreDbModel[]) => {
          stores = stores?.length ? this.helperService.sortBy(stores, 'name') : [];
          this.setStoresInLocalStorage(stores);
          return of(stores);
        })
      );
  }

  create(model: FormData): Observable<StoreDbModel> {
    return this.httpClientService.post('stores', model, {})
      .pipe(
        switchMap((stores: StoreDbModel) => {
          this.localstorageService.remove(LOCAL_STORAGE_CONSTANT.STORES_KEY);
          return of(stores);
        })
      );
  }

  edit(data: FormData, uuid: string): Observable<StoreDbModel> {
    return this.httpClientService.post(`stores/update/${uuid}`, data, {})
      .pipe(
        switchMap((store: StoreDbModel) => {
          this.localstorageService.remove(LOCAL_STORAGE_CONSTANT.STORES_KEY);
          this.workerService.clearWorkersFromLocalStorage();
          return of(store);
        })
      );
  }

  /**
   * `reparentChildrenToUuid` accepts the literal string `'null'` (not the JS value `null`) to
   * make live children top-level — mirrors P0's `reparent_children_to_uuid` query param exactly;
   * the backend distinguishes "param absent" (400 when the store has live children) from
   * "param is the string null" (re-parent to root).
   */
  delete(uuid: string, migratedStoreUuid?: string, reparentChildrenToUuid?: string): Observable<{ success: boolean }> {
    const params: Params = {};
    if (migratedStoreUuid) {
      params.migrate_store_uuid = migratedStoreUuid;
    }
    if (reparentChildrenToUuid) {
      params.reparent_children_to_uuid = reparentChildrenToUuid;
    }
    return this.httpClientService.delete(`stores/${uuid}`, { params }).pipe(
      switchMap((result: { success: boolean }) => {
        if (result?.success) {
          this.localstorageService.remove(LOCAL_STORAGE_CONSTANT.STORES_KEY);
        }
        return of(result);
      })
    );
  }

  setStoresInLocalStorage(stores: StoreDbModel[]): void {
    const encryptedString = this.cryptoService.encryptValue(JSON.stringify(stores));
    this.localstorageService.set(LOCAL_STORAGE_CONSTANT.STORES_KEY, encryptedString);
  }

  getStoresFromLocalStorage(): StoreDbModel[] {
    const encryptedStores: string = this.localstorageService.get(LOCAL_STORAGE_CONSTANT.STORES_KEY);
    if (encryptedStores) {
      const decryptedStores = this.cryptoService.decryptValue(encryptedStores);
      const stores: StoreDbModel[] = JSON.parse(decryptedStores);
      return stores;
    } else {
      return null;
    }
  }

  multipleEdit(stores: StoreDbModel[]): Observable<{ success: boolean, stores: StoreDbModel[] }> {
    return this.httpClientService.post(`stores/multiple_edit`, stores, {})
      .pipe(
        switchMap((result: { success: boolean, stores: StoreDbModel[] }) => {
          if (result?.stores?.length) {
            this.setStoresInLocalStorage(result?.stores);
          }
          return of(result);
        })
      );
  }

  getExivoComponents(uuid: string): Observable<ExivoComponentDbModel[]> {
    return this.httpClientService.get(`stores/get_exivo_components/${uuid}`, {});
  }

  getMappedEventStores(): Observable<StoreDbModel[]> {
    return this.httpClientService.get('stores/get_event_stores', {});
  }

  getStoresWithWorkers(body?: { worker_id?: number, only_bookable_via_deeplink?: boolean }): Observable<StoreDbModel[]> {
    return this.httpClientService.post('stores/stores_with_workers', body, {}).pipe(
      switchMap(stores => {
        return of(stores?.length ? this.helperService.sortByNumber(stores, 'position') : []);
      })
    );
  }
}
